import { useEffect, useState, useRef, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Dashboard from "./pages/Dashboard";
import AlumniDirectory from "./pages/AlumniDirectory";
import EnrollmentWizard from "./pages/EnrollmentWizard";
import Documents from "./pages/Documents";
import Courses from "./pages/Courses"; // kept available for any future use; route now redirects /courses → /programs
import CourseDetail from "./pages/CourseDetail";
import CourseLearn from "./pages/CourseLearn";
import CourseEditor from "./pages/CourseEditor";
import ProgramsPage from "./pages/ProgramsPage";
import AcademicCatalog from "./pages/AcademicCatalog";
import Mentorship from "./pages/Mentorship";
import TalentDatabase from "./pages/TalentDatabase";
import SkillsMatrix from "./pages/SkillsMatrix";
import Alumni from "./pages/Alumni";
import Profile from "./pages/Profile";
import AdminPanel from "./pages/AdminPanel";
import InstructorDashboard from "./pages/InstructorDashboard";
import FAQPage from "./pages/FAQPage";
import CommunityFeed from "./pages/CommunityFeed";
import WorkingStyleQuiz from "./pages/WorkingStyleQuiz";
import LiveClassesMockup from "./pages/LiveClassesMockup";
import VerifyCertification from "./pages/VerifyCertification";
import WorkforceDirectoryPage from "./pages/WorkforceDirectoryPage";
import WorkforceLookupPublic from "./pages/WorkforceLookupPublic";
import ContactPage from "./pages/ContactPage";
import TalentSignup from "./pages/TalentSignup";
import StrengthsProfile from "./pages/StrengthsProfile";
import SetPasswordPage from "./pages/SetPasswordPage";
import SignDocumentPage from "./pages/SignDocumentPage";
import StoriesPage from "./pages/StoriesPage";
import CohortRoster from "./pages/CohortRoster";
import ClassesPage from "./pages/ClassesPage";
import WelcomePacket from "./pages/WelcomePacket";
import Messages from "./pages/Messages";
import GrievancePolicy from "./pages/GrievancePolicy";
import WelcomePacketCybersecurity from "./pages/WelcomePacketCybersecurity";
import SignupChooser from "./pages/SignupChooser";
import MentorSignup from "./pages/MentorSignup";
import MentorGuide from "./pages/MentorGuide";
import JoinCommunity from "./pages/JoinCommunity";
import ChatBot from "./components/ChatBot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithPassword = async (email, password) => {
    const response = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });
    if (!response.ok) {
      const err = await response.json();
      const detail = err.detail;
      if (typeof detail === 'string') throw new Error(detail);
      if (Array.isArray(detail)) throw new Error(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      throw new Error('Login failed');
    }
    const data = await response.json();
    // MFA challenge or forced setup — caller handles UI
    if (data.mfa_required || data.mfa_setup_required) {
      return data;
    }
    setUser(data);
    return data;
  };

  const registerWithPassword = async (email, password, name) => {
    const response = await fetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name })
    });
    if (!response.ok) {
      const err = await response.json();
      const detail = err.detail;
      if (typeof detail === 'string') throw new Error(detail);
      if (Array.isArray(detail)) throw new Error(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      throw new Error('Registration failed');
    }
    const userData = await response.json();
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
      setUser(null);
      toast.success("Logged out successfully");
    } catch {
      toast.error("Logout failed");
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, loginWithPassword, registerWithPassword, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Handler
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate('/');
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          toast.success(`Welcome, ${userData.name}!`);
          navigate('/dashboard', { replace: true, state: { user: userData } });
        } else {
          toast.error("Authentication failed");
          navigate('/');
        }
      } catch {
        toast.error("Authentication error");
        navigate('/');
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#FFD507] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-300">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, login } = useAuth();
  const location = useLocation();

  // If user data was passed from AuthCallback, use it
  if (location.state?.user && !user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FFD507] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-8 text-center max-w-md">
          <h2 className="text-2xl font-heading font-bold text-[#0F1B3D] mb-4">Sign In Required</h2>
          <p className="text-slate-500 mb-6">Please sign in to access this page.</p>
          <div className="space-y-3">
            <button onClick={login} className="btn-primary w-full" data-testid="login-redirect-btn">
              Sign In with Google
            </button>
            <a href="/login" className="block text-sm text-[#1E3A8A] hover:underline" data-testid="login-email-link">
              Or sign in with email &amp; password
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id - do this BEFORE routing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/apply" element={<EnrollmentWizard />} />
      <Route path="/alumni-directory" element={
        <ProtectedRoute>
          <AlumniDirectory />
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <Documents />
        </ProtectedRoute>
      } />
      <Route path="/programs" element={<ProgramsPage />} />
      <Route path="/catalog" element={<AcademicCatalog />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/grievance-policy" element={<GrievancePolicy />} />
      <Route path="/welcome/cybersecurity" element={<WelcomePacketCybersecurity />} />
      <Route path="/community" element={
        <ProtectedRoute>
          <CommunityFeed />
        </ProtectedRoute>
      } />
      <Route path="/courses" element={<Navigate to="/programs" replace />} />
      <Route path="/courses/:courseId" element={<CourseDetail />} />
      <Route path="/courses/:courseId/learn" element={
        <ProtectedRoute>
          <CourseLearn />
        </ProtectedRoute>
      } />
      <Route path="/mentorship" element={
        <ProtectedRoute>
          <Mentorship />
        </ProtectedRoute>
      } />
      <Route path="/skills-matrix" element={<SkillsMatrix />} />
      <Route path="/talent" element={
        <ProtectedRoute>
          <TalentDatabase />
        </ProtectedRoute>
      } />
      <Route path="/alumni" element={
        <ProtectedRoute>
          <Alumni />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/working-style" element={
        <ProtectedRoute>
          <WorkingStyleQuiz />
        </ProtectedRoute>
      } />
      <Route path="/instructor/live-classes-preview" element={
        <ProtectedRoute>
          <LiveClassesMockup />
        </ProtectedRoute>
      } />
      <Route path="/verify/:verificationId" element={<VerifyCertification />} />
      <Route path="/workforce-lookup" element={<WorkforceLookupPublic />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/talent-signup" element={<TalentSignup />} />
      <Route path="/signup" element={<SignupChooser />} />
      <Route path="/mentor-signup" element={<MentorSignup />} />
      <Route path="/mentor-guide" element={<MentorGuide />} />
      <Route path="/join-community" element={<JoinCommunity />} />
      <Route path="/strengths" element={<ProtectedRoute><StrengthsProfile /></ProtectedRoute>} />
      <Route path="/set-password/:token" element={<SetPasswordPage />} />
      <Route path="/sign" element={<ProtectedRoute><SignDocumentPage /></ProtectedRoute>} />
      <Route path="/sign/:requestId" element={<ProtectedRoute><SignDocumentPage /></ProtectedRoute>} />
      <Route path="/stories" element={<StoriesPage />} />
      <Route path="/admin/workforce-directory" element={
        <ProtectedRoute><WorkforceDirectoryPage /></ProtectedRoute>
      } />
      <Route path="/admin/cohorts/:cohortId/roster" element={
        <ProtectedRoute><CohortRoster /></ProtectedRoute>
      } />
      <Route path="/instructor/classes" element={
        <ProtectedRoute><ClassesPage /></ProtectedRoute>
      } />
      <Route path="/programs/:programId/welcome" element={
        <ProtectedRoute><WelcomePacket /></ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute><Messages /></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin', 'instructor']}>
          <AdminPanel />
        </ProtectedRoute>
      } />
      <Route path="/instructor" element={
        <ProtectedRoute allowedRoles={['admin', 'instructor']}>
          <InstructorDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/courses/new" element={
        <ProtectedRoute allowedRoles={['admin', 'instructor']}>
          <CourseEditor />
        </ProtectedRoute>
      } />
      <Route path="/admin/courses/:courseId/edit" element={
        <ProtectedRoute allowedRoles={['admin', 'instructor']}>
          <CourseEditor />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRouter />
        <ChatBot />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API };
